// Help: the topic list on the left (searchable, grouped like the app's navigation) and the article
// on the right. Every article has its own URL (/help/<id>) and its sections their own anchors
// (/help/<id>#<section>), which the field-help modals link to.
import { useEffect, useState } from "preact/hooks";
import { ARTICLES, articleById, groupArticles, searchArticles, type ArticleId } from "../../help/articles";
import { ARTICLE_CONTENT } from "../../help/content";
import type { ScreenProps } from "../../router/route";
import { Icon } from "@nexus/ui";
import { Link, useBreadcrumb } from "../../ui/shell";
import { NotFound } from "@nexus/ui";

const FIRST: ArticleId = "what-nexus-does";

export function HelpScreen({ params }: ScreenProps<"/help/:article"> | ScreenProps<"/help">) {
  const id = "article" in params ? params.article : FIRST;
  const article = articleById(id);
  const [query, setQuery] = useState("");
  const found = searchArticles(query);
  const groups = groupArticles(query.trim() === "" ? ARTICLES : found);

  useBreadcrumb(article ? [{ label: article.group }, { label: article.title }] : null);

  // Land on the section a help modal linked to, else at the top of the article.
  useEffect(() => {
    const hash = decodeURIComponent(window.location.hash.replace(/^#/, ""));
    const target = hash ? document.getElementById(hash) : null;
    if (target) target.scrollIntoView({ block: "start" });
    else window.scrollTo(0, 0);
  }, [id]);

  const Body = article ? ARTICLE_CONTENT[article.id as ArticleId] : null;

  return (
    <div class="help-layout">
      <nav class="help-nav" aria-label="Help topics">
        <div class="search-input">
          <Icon name="search" />
          <input
            type="search"
            aria-label="Search help"
            placeholder="Search help"
            value={query}
            onInput={(event) => setQuery(event.currentTarget.value)}
          />
        </div>
        {groups.length === 0 && <p class="help-no-results">No article matches “{query}”.</p>}
        {groups.map((group) => (
          <div key={group.group} class="stack-sm">
            <div class="nav-heading">{group.group}</div>
            <ul class="help-nav-group">
              {group.articles.map((entry) => (
                <li key={entry.id}>
                  <Link href={`/help/${entry.id}`} class="help-nav-link" current={entry.id === article?.id}>
                    {entry.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      {article && Body ? (
        <article class="help-article" aria-labelledby="help-article-title">
          <span class="help-article-kicker">{article.group}</span>
          <h1 id="help-article-title">{article.title}</h1>
          <Body />
        </article>
      ) : (
        <div class="help-article">
          <NotFound />
        </div>
      )}
    </div>
  );
}
